import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { TreeNode } from 'primereact/treenode';
import Tooltip from 'components/UI/Tooltip';
import { useStores } from 'stores/hooks/hooks';
import {
  ResellerTreeNode,
  CustomTreeNode,
  ResellerTreeMode,
} from 'models/grower';
import Loading from 'components/UI/Loading/Loading';

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledContentContainer = styled.li`
  flex: 1;
  flex-direction: column;
  display: flex;
  overflow: auto;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const StyledRow = styled.div`
  font-size: 1rem;
  color: #495057;
  min-height: 32px;
  align-content: center;
  padding: 0px 1rem;
`;

interface ResellerTreeProps {
  searchText?: string;
  // When arriving via a grower-id deep link, auto-open this grower once it shows
  // up in the (uncapped) search results — so the link lands on the grower's
  // plots without a manual click.
  deeplinkGrowerId?: number | null;
}

const ResellersSearchTree: FC<ResellerTreeProps> = observer(
  ({ searchText, deeplinkGrowerId }) => {
    const { resellersStore } = useStores();

    const resellerSearchTreeNodeList = useMemo(() => {
      return resellersStore.resellerSearchTree?.data?.map((node) => ({
        key: node.id,
        label: `${node.name} (${node.id})`,
        data: node,
        icon: null,
        className: '',
        children: undefined,
      }));
    }, [resellersStore.resellerSearchTree.data]);

    // Auto-select the deep-linked grower once (when its search result arrives),
    // so a grower-id link opens the grower directly instead of stopping at the
    // list. Guarded so it never hijacks subsequent manual searches.
    const hasAutoSelected = useRef(false);
    useEffect(() => {
      if (
        hasAutoSelected.current ||
        deeplinkGrowerId == null ||
        resellersStore.resellerSearchTree.loading
      ) {
        return;
      }

      const match = resellerSearchTreeNodeList?.find(
        (node) => node.data.id === deeplinkGrowerId,
      );

      if (match) {
        hasAutoSelected.current = true;
        resellersStore.setSelectedTreeNode(
          match as TreeNode as CustomTreeNode<ResellerTreeNode>,
          ResellerTreeMode.All,
        );
      }
    }, [deeplinkGrowerId, resellerSearchTreeNodeList, resellersStore]);

    const onSelect = useCallback(
      (node: TreeNode) => {
        const isSameNodeSelected =
          node.data.id === resellersStore.selectedGrowerId;

        if (isSameNodeSelected) {
          return;
        }

        resellersStore.setSelectedTreeNode(
          node as CustomTreeNode<ResellerTreeNode>,
          ResellerTreeMode.All,
        );
      },
      [resellersStore],
    );

    if (resellersStore.resellerSearchTree.loading) {
      return (
        <StyledContentContainer>
          <Loading />
        </StyledContentContainer>
      );
    }

    return (
      <StyledContentContainer>
        <List>
          {resellerSearchTreeNodeList.map((node) => (
            <Tooltip title={node.label}>
              <StyledRow
                style={{
                  backgroundColor:
                    node.data.id === resellersStore.selectedGrowerId
                      ? 'var(--highlight-bg)'
                      : 'transparent',
                }}
                key={node.key}
                onClick={() => onSelect(node)}
              >
                {node.label}
              </StyledRow>
            </Tooltip>
          ))}
        </List>
      </StyledContentContainer>
    );
  },
);

export default ResellersSearchTree;
